import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Label } from '@easyflow/ui-master/components/ui/label';
import { Alert, AlertDescription } from '@easyflow/ui-master/components/ui/alert';
import { TotpCodeInput } from '@easyflow/ui-master/components/ui/totp-code-input';
import { TotpQrSetup } from '@easyflow/ui-master/components/ui/totp-qr-setup';
import { BackupCodesViewer } from '@easyflow/ui-master/components/ui/backup-codes-viewer';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@easyflow/ui-master/components/ui/alert-dialog';
import {
  Loader2, Shield, Building2, Eye, EyeOff, Lock, User,
  Users, BarChart3, Server, Globe, Database, ExternalLink, AlertTriangle,
  KeyRound, ArrowLeft, ShieldCheck, CheckCircle, X as XIcon,
} from 'lucide-react';
import apiClient from '@/lib/api';
import edgLogoWhite from '@/assets/edg-logo-white.png';
import edgMarkWhite from '@/assets/edg-mark-white.png';
import { buildCrmUrl, sessionPolicySchema } from '@easyflow/shared';
import type {
  MasterTwoFactorSetupResponse,
  MasterTwoFactorEnableRequest,
} from '@easyflow/shared';
import { getCurrentAdminSurfaceContext } from '@/lib/paths';

type LoginVariant = 'master' | 'crm';

interface LoginFeature {
  icon: typeof Shield;
  title: string;
  description: string;
  iconBg: string;
  iconColor: string;
}

const features: LoginFeature[] = [
  {
    icon: Building2,
    title: 'Gestione Multi-Tenant',
    description: 'Crea, configura e monitora tutti i tenant dalla dashboard centralizzata',
    iconBg: 'bg-purple-500/10 dark:bg-purple-500/20',
    iconColor: 'text-purple-600 dark:text-purple-300',
  },
  {
    icon: Users,
    title: 'Amministrazione Utenti',
    description: 'Gestisci operatori, permessi e ruoli per ogni tenant del sistema',
    iconBg: 'bg-indigo-500/10 dark:bg-indigo-500/20',
    iconColor: 'text-indigo-600 dark:text-indigo-300',
  },
  {
    icon: BarChart3,
    title: 'Analytics Cross-Tenant',
    description: 'Visualizza metriche aggregate, utilizzo risorse e performance globali',
    iconBg: 'bg-violet-500/10 dark:bg-violet-500/20',
    iconColor: 'text-violet-600 dark:text-violet-300',
  },
  {
    icon: Server,
    title: 'Infrastruttura VoIP',
    description: 'Monitora FreeSWITCH, trunk SIP, endpoint e salute del sistema',
    iconBg: 'bg-fuchsia-500/10 dark:bg-fuchsia-500/20',
    iconColor: 'text-fuchsia-600 dark:text-fuchsia-300',
  },
  {
    icon: Database,
    title: 'Gestione Dati',
    description: 'Backup, migrazioni e manutenzione database per tutti i tenant',
    iconBg: 'bg-pink-500/10 dark:bg-pink-500/20',
    iconColor: 'text-pink-600 dark:text-pink-300',
  },
];

interface LoginStat {
  value: string;
  label: string;
}

// KPI reali EasyDigital Group (fonte: easydigitalgroup.it) — card dedicate
// per la variante EasyCRM del login master.
const edgStats: LoginStat[] = [
  { value: '2004', label: 'Anno di fondazione' },
  { value: '150+', label: 'Persone nel gruppo' },
  { value: '28', label: 'Negozi sul territorio' },
  { value: '~50K', label: 'Clienti B2C / mese' },
  { value: '~900', label: 'Aziende B2B / mese' },
];

// Ticker istituzionale, stesso motivo del sito EasyDigital Group.
const edgTicker: string[] = [
  'Easy Retail',
  'Easy Solutions',
  'Easy Agency',
  'Connect',
  'Build',
  'Evolve',
  'Retail-tech',
  'AI Driven',
  'Consulting',
];

interface LoginBranding {
  brandName: string;
  brandTagline: string;
  brandIcon: typeof Shield;
  brandGradient: string;
  intro: string;
  features?: LoginFeature[];
  stats?: LoginStat[];
  logoSrc?: string;
  markSrc?: string;
  claim?: string;
  ticker?: string[];
  formHeading: string;
  formSubtitle: string;
  footerTitle: string;
  footerSubtitle: string;
}

const LOGIN_BRANDING: Record<LoginVariant, LoginBranding> = {
  master: {
    brandName: 'EasyFlow',
    brandTagline: 'Master Admin',
    brandIcon: Shield,
    brandGradient: 'from-purple-600 to-violet-600',
    intro: "Pannello di controllo centralizzato per la gestione dell'infrastruttura multi-tenant",
    features,
    formHeading: 'Master Tenant Login',
    formSubtitle: 'Accesso riservato agli amministratori',
    footerTitle: 'EasyFlow Master Dashboard',
    footerSubtitle: 'Gestione cross-tenant e analytics',
  },
  crm: {
    brandName: 'EasyDigital Group',
    brandTagline: 'Retail-tech · AI · Consulting',
    brandIcon: Building2,
    brandGradient: 'from-[#534AB7] to-[#7F77DD]',
    intro:
      "Connettiamo, costruiamo e accompagniamo l'evoluzione delle aziende. Il CRM del gruppo: clienti, pipeline, preventivi e fatturazione in un unico posto.",
    stats: edgStats,
    logoSrc: edgLogoWhite,
    markSrc: edgMarkWhite,
    claim: 'Connect. Build. Evolve.',
    ticker: edgTicker,
    formHeading: 'EasyCRM Login',
    formSubtitle: 'Accesso riservato agli operatori del gruppo',
    footerTitle: 'EasyDigital Group',
    footerSubtitle: 'Connect · Build · Evolve',
  },
};

type Phase = 'credentials' | '2fa-totp' | '2fa-backup' | 'totp-setup' | 'force-password-change';

type SetupStep = 0 | 1 | 2;

interface PasswordCriterion {
  key: string;
  label: string;
  test: (p: string) => boolean;
}

// Criteri di sicurezza allineati 1:1 alla login tenant (Login.tsx).
const PASSWORD_CRITERIA: PasswordCriterion[] = [
  { key: 'length', label: 'Almeno 12 caratteri', test: (p) => p.length >= 12 },
  { key: 'upper', label: 'Almeno una maiuscola (A-Z)', test: (p) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'Almeno una minuscola (a-z)', test: (p) => /[a-z]/.test(p) },
  { key: 'number', label: 'Almeno un numero (0-9)', test: (p) => /[0-9]/.test(p) },
  {
    key: 'special',
    label: 'Almeno un carattere speciale (!@#$%...)',
    test: (p) => /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/~`]/.test(p),
  },
];

interface BffLoginResponseData {
  token?: string;
  refreshToken?: string;
  /** Policy di sessione risolta dal server (validata con `sessionPolicySchema` prima dell'uso). */
  sessionPolicy?: unknown;
  user?: { is_master_tenant?: boolean };
}

interface BffLoginResponse {
  success?: boolean;
  data?: BffLoginResponseData;
  error?: string | { message?: string };
  requires2FA?: boolean;
  requiresTOTPSetup?: boolean;
  forcePasswordChange?: boolean;
  tempToken?: string;
  message?: string;
  attemptsRemaining?: number;
  activeSessions?: unknown;
  user?: { firstName?: string; lastName?: string; email?: string };
}

interface ForceChangePasswordResponse {
  success?: boolean;
  error?: string;
  message?: string;
  details?: string[];
}

const INPUT_TOKENIZED =
  'bg-input border-input text-foreground placeholder:text-muted-foreground ' +
  'focus:ring-2 focus:ring-ring transition-all duration-200';

interface MasterLoginProps {
  variant?: LoginVariant;
}

export default function MasterLogin({ variant = 'master' }: MasterLoginProps): JSX.Element {
  const branding = LOGIN_BRANDING[variant];
  const BrandIcon = branding.brandIcon;
  const isCrm = variant === 'crm';
  const accentText = isCrm ? 'text-[#534AB7]' : 'text-purple-700';
  const formGradient = isCrm
    ? 'from-[#534AB7] via-[#4b43a6] to-[#2f2b66]'
    : 'from-purple-600 via-purple-500 to-violet-600';
  const [phase, setPhase] = useState<Phase>('credentials');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [showActiveSessionDialog, setShowActiveSessionDialog] = useState<boolean>(false);

  // Task #3585 — Self-service "password dimenticata" pubblico: il link email
  // atterra su /reset-password (dual-mode, base-aware).
  const [forgotOpen, setForgotOpen] = useState<boolean>(false);
  const [forgotEmail, setForgotEmail] = useState<string>('');
  const [forgotBusy, setForgotBusy] = useState<boolean>(false);
  const [forgotDone, setForgotDone] = useState<boolean>(false);
  const [forgotError, setForgotError] = useState<string | null>(null);

  const handleForgotSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (forgotBusy || forgotEmail.trim().length === 0) return;
    setForgotBusy(true);
    setForgotError(null);
    try {
      const response = await fetch('/api/master/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });
      if (response.ok) {
        setForgotDone(true);
      } else {
        const data = (await response.json().catch(() => null)) as { error?: string } | null;
        setForgotError(data?.error || 'Richiesta non riuscita. Riprova tra qualche minuto.');
      }
    } catch {
      setForgotError('Errore di rete. Riprova tra qualche istante.');
    } finally {
      setForgotBusy(false);
    }
  };

  // 2FA challenge state
  const [totpCode, setTotpCode] = useState<string>('');
  const [backupCode, setBackupCode] = useState<string>('');
  const [twoFAError, setTwoFAError] = useState<string | null>(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);

  // Forced TOTP enrollment state (flag "2FA obbligatorio" ON + utente non iscritto)
  const [setupTempToken, setSetupTempToken] = useState<string>('');
  const [setupData, setSetupData] = useState<MasterTwoFactorSetupResponse | null>(null);
  const [setupStep, setSetupStep] = useState<SetupStep>(0);
  const [setupCode, setSetupCode] = useState<string>('');
  const [setupAck, setSetupAck] = useState<boolean>(false);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [setupBusy, setSetupBusy] = useState<boolean>(false);

  // Forced password change state (flag `force_password_change=true`, es. Admin
  // CRM appena provisionato con password provvisoria). Il backend emette il
  // payload DOPO la verifica 2FA.
  const [forceChangeToken, setForceChangeToken] = useState<string>('');
  const [forceChangeUserName, setForceChangeUserName] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [changePwdBusy, setChangePwdBusy] = useState<boolean>(false);
  const [changePwdError, setChangePwdError] = useState<string | null>(null);

  const navigate = useNavigate();
  const { login, isAuthenticated, user } = useAuth();
  const surfaceContext = getCurrentAdminSurfaceContext();

  useEffect(() => {
    if (!isAuthenticated) return;
    // Gli admin CRM atterrano sempre sulla loro superficie: host dedicato per
    // il cutover, path legacy solo finché una configurazione locale lo serve.
    const isSuperAdmin = user?.role === 'super_admin';
    if (!isSuperAdmin) {
      if (surfaceContext.surface === 'master') {
        window.location.replace(buildCrmUrl('/'));
      } else {
        navigate(surfaceContext.homePath, { replace: true });
      }
      return;
    }
    navigate(variant === 'crm' ? surfaceContext.homePath : '/dashboard', { replace: true });
  }, [isAuthenticated, user, navigate, surfaceContext.homePath, surfaceContext.surface, variant]);

  const performLogin = async (
    forceLogin: boolean = false,
    totp?: string,
    backup?: string,
  ): Promise<void> => {
    setLoading(true);
    setError('');
    setTwoFAError(null);

    try {
      const body: Record<string, unknown> = { tenant: 'master', email, password };
      if (forceLogin) body.forceLogin = true;
      if (totp) body.totpCode = totp;
      if (backup) body.backupCode = backup;

      const response = await fetch('/api/bff/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (response.status === 409) {
        const errorData = (await response.json().catch(() => ({}))) as BffLoginResponse;
        if (errorData.activeSessions) {
          setShowActiveSessionDialog(true);
          setLoading(false);
          return;
        }
      }

      const data = (await response.json().catch(() => ({}))) as BffLoginResponse;

      // Forced TOTP enrollment: flag "2FA obbligatorio" ON + utente non iscritto.
      // Il backend emette un tempToken a scopo limitato (`purpose: 'totp_setup'`)
      // che consente SOLO di completare l'iscrizione 2FA.
      if (data.requiresTOTPSetup && data.tempToken) {
        await startForcedSetup(data.tempToken);
        return;
      }

      // Cambio password obbligatorio: il backend emette questo payload DOPO la
      // verifica 2FA (200 OK, senza token di sessione). Può arrivare dal primo
      // login diretto, dopo la challenge 2FA (TOTP/backup) o al termine
      // dell'enrollment 2FA forzato (che rilancia il login completo).
      if (data.forcePasswordChange && data.tempToken) {
        setForceChangeToken(data.tempToken);
        setForceChangeUserName(data.user?.firstName ?? '');
        setNewPassword('');
        setConfirmPassword('');
        setChangePwdError(null);
        setPhase('force-password-change');
        setLoading(false);
        return;
      }

      // 2FA challenge required: switch to TOTP step (no full reload)
      if (data.requires2FA && !data.data?.token) {
        // If we already submitted a code, treat as error
        if (totp || backup) {
          const msg = backup
            ? 'Codice di backup non valido o già utilizzato.'
            : 'Codice non valido. Controlla l\'app di autenticazione e riprova.';
          setTwoFAError(msg);
          setAttemptsRemaining(
            typeof data.attemptsRemaining === 'number' ? data.attemptsRemaining : null,
          );
          if (backup) setBackupCode('');
          else setTotpCode('');
          setLoading(false);
          return;
        }
        setPhase('2fa-totp');
        setTotpCode('');
        setBackupCode('');
        setLoading(false);
        return;
      }

      if (data.success && data.data?.token) {
        if (!data.data.user?.is_master_tenant) {
          throw new Error('Accesso negato. Richiesti privilegi Master Tenant Admin.');
        }

        const sessionPolicy = sessionPolicySchema.safeParse(data.data.sessionPolicy);
        await login(
          data.data.token,
          data.data.refreshToken,
          sessionPolicy.success ? sessionPolicy.data : undefined,
        );
      } else {
        const errMsg = typeof data.error === 'string'
          ? data.error
          : data.error?.message ?? data.message ?? 'Login fallito';
        throw new Error(errMsg);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Login fallito';
      if (phase === 'credentials') setError(msg);
      else setTwoFAError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    await performLogin(false);
  };

  const handleForceLogin = async (): Promise<void> => {
    setShowActiveSessionDialog(false);
    await performLogin(true);
  };

  const handle2FASubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (phase === '2fa-totp') {
      const code = totpCode.replace(/\s/g, '');
      if (code.length !== 6) return;
      await performLogin(false, code);
    } else if (phase === '2fa-backup') {
      const code = backupCode.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (!/^[A-Z0-9]{8}$/.test(code)) {
        setTwoFAError('Il codice di backup deve essere di 8 caratteri alfanumerici.');
        return;
      }
      await performLogin(false, undefined, code);
    }
  };

  const resetToCredentials = (): void => {
    setPhase('credentials');
    setTotpCode('');
    setBackupCode('');
    setTwoFAError(null);
    setAttemptsRemaining(null);
  };

  // Avvia l'enrollment forzato: usa il tempToken come Bearer per generare il
  // segreto TOTP (QR + backup codes) via /master/2fa/setup.
  const startForcedSetup = async (tempToken: string): Promise<void> => {
    setSetupBusy(true);
    setSetupError(null);
    try {
      apiClient.setToken(tempToken);
      const res = await apiClient.post<MasterTwoFactorSetupResponse>('/master/2fa/setup', {});
      if (res.success && res.data) {
        setSetupTempToken(tempToken);
        setSetupData(res.data);
        setSetupStep(0);
        setSetupCode('');
        setSetupAck(false);
        setPhase('totp-setup');
      } else {
        apiClient.clearToken();
        setError(res.error ?? 'Errore avvio configurazione 2FA. Riprova.');
      }
    } catch (err) {
      apiClient.clearToken();
      setError(err instanceof Error ? err.message : 'Errore avvio configurazione 2FA. Riprova.');
    } finally {
      setSetupBusy(false);
      setLoading(false);
    }
  };

  // Conferma il codice TOTP e attiva il 2FA via /master/2fa/enable.
  const confirmForcedEnable = async (code: string): Promise<void> => {
    const clean = code.replace(/\s/g, '');
    if (clean.length !== 6) {
      setSetupError('Inserisci le 6 cifre.');
      return;
    }
    setSetupBusy(true);
    setSetupError(null);
    try {
      apiClient.setToken(setupTempToken);
      const body: MasterTwoFactorEnableRequest = { code: clean };
      const res = await apiClient.post('/master/2fa/enable', body);
      if (res.success) {
        setSetupStep(2);
      } else {
        setSetupError(res.error ?? 'Codice non valido. Riprova.');
        setSetupCode('');
      }
    } catch (err) {
      setSetupError(err instanceof Error ? err.message : 'Codice non valido. Riprova.');
      setSetupCode('');
    } finally {
      setSetupBusy(false);
    }
  };

  // Conclude l'enrollment: scarta il tempToken e torna alle credenziali per un
  // login completo (ora con challenge TOTP).
  const finishForcedSetup = (): void => {
    apiClient.clearToken();
    setSetupTempToken('');
    setSetupData(null);
    setSetupStep(0);
    setSetupCode('');
    setSetupAck(false);
    setSetupError(null);
    setPassword('');
    setPhase('credentials');
    setError('2FA configurato correttamente. Accedi di nuovo con email, password e codice 2FA.');
  };

  const cancelForcedSetup = (): void => {
    apiClient.clearToken();
    setSetupTempToken('');
    setSetupData(null);
    setSetupStep(0);
    setSetupCode('');
    setSetupAck(false);
    setSetupError(null);
    setPhase('credentials');
  };

  // Invia la nuova password al backend con il tempToken `force_password_change`.
  // In caso di successo torna alle credenziali per un login pulito.
  const handleForcePasswordChange = async (): Promise<void> => {
    if (newPassword !== confirmPassword) {
      setChangePwdError('Le password non coincidono.');
      return;
    }
    if (!PASSWORD_CRITERIA.every((c) => c.test(newPassword))) {
      setChangePwdError('La password non rispetta tutti i criteri di sicurezza.');
      return;
    }
    setChangePwdBusy(true);
    setChangePwdError(null);
    try {
      const response = await fetch('/api/master/force-change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempToken: forceChangeToken, newPassword }),
      });
      const data = (await response.json().catch(() => ({}))) as ForceChangePasswordResponse;
      if (data.success) {
        setForceChangeToken('');
        setNewPassword('');
        setConfirmPassword('');
        setPassword('');
        setPhase('credentials');
        setError('Password aggiornata con successo. Accedi con la tua email e la nuova password.');
      } else {
        const detail = Array.isArray(data.details) ? data.details.join(', ') : undefined;
        setChangePwdError(data.error ?? detail ?? 'Errore nel cambio password.');
      }
    } catch (err) {
      setChangePwdError(err instanceof Error ? err.message : 'Errore di connessione.');
    } finally {
      setChangePwdBusy(false);
    }
  };

  const cancelForcePasswordChange = (): void => {
    setForceChangeToken('');
    setForceChangeUserName('');
    setNewPassword('');
    setConfirmPassword('');
    setChangePwdError(null);
    setShowNewPassword(false);
    setPhase('credentials');
  };

  return (
    <div
      className={`min-h-screen ${isCrm ? 'text-white' : 'bg-background'}`}
      style={
        isCrm
          ? {
              backgroundColor: '#050506',
              backgroundImage:
                'radial-gradient(60% 55% at 50% 35%, rgba(83,74,183,0.20) 0%, rgba(5,5,6,0) 70%)',
            }
          : undefined
      }
    >
      {isCrm && (
        <style>{`
          @keyframes edg-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
          .edg-marquee { animation: edg-marquee 24s linear infinite; width: max-content; }
        `}</style>
      )}
      <div className="flex flex-col lg:flex-row min-h-screen">

        {/* Left Side - Features */}
        <div className="lg:w-1/2 flex flex-col justify-center p-8 lg:p-12 xl:p-16">
          <div className="mb-8 lg:mb-12">
            {isCrm && branding.logoSrc ? (
              <>
                <img
                  src={branding.logoSrc}
                  alt={branding.brandName}
                  className="h-9 lg:h-11 w-auto object-contain mb-6"
                />
                {branding.claim && (
                  <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">
                    {branding.claim}
                  </h1>
                )}
                <p className="text-lg text-white/70 max-w-xl">{branding.intro}</p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${branding.brandGradient} flex items-center justify-center shadow-lg`}>
                    <BrandIcon className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl lg:text-4xl font-bold text-foreground">{branding.brandName}</h1>
                    <span className="text-sm font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider">{branding.brandTagline}</span>
                  </div>
                </div>
                <p className="text-lg text-muted-foreground">
                  {branding.intro}
                </p>
              </>
            )}
          </div>

          {branding.stats ? (
            <div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {branding.stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="border border-white/10 bg-white/[0.03] p-4 backdrop-blur-sm transition-colors duration-200 hover:border-[#7F77DD]/50"
                  >
                    <div className="text-3xl font-bold text-white tracking-tight">{stat.value}</div>
                    <div className="text-xs text-white/50 uppercase tracking-wider mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
              {branding.ticker && (
                <div className="mt-8 relative overflow-hidden border-y border-white/10 py-3">
                  <div className="edg-marquee flex items-center gap-6 whitespace-nowrap text-xs uppercase tracking-[0.2em] text-white/40">
                    {[...branding.ticker, ...branding.ticker].map((label, index) => (
                      <span key={`${label}-${index}`} className="inline-flex items-center gap-6">
                        {label}
                        <span className="text-[#7F77DD]">·</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : branding.features ? (
            <div className="space-y-4">
              {branding.features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-4 bg-card text-card-foreground rounded-xl border border-border shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  <div className={`flex-shrink-0 w-12 h-12 rounded-lg ${feature.iconBg} flex items-center justify-center`}>
                    <feature.icon className={`h-6 w-6 ${feature.iconColor}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground mt-0.5">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {/* Right Side - Login Form */}
        <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-md">
            <div className={`bg-gradient-to-br ${formGradient} rounded-2xl p-8 shadow-2xl transition-all duration-300`}>

              {/* Header */}
              <div className="text-center mb-8">
                <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full
                              bg-white/20 backdrop-blur-sm border border-white/30 shadow-lg mb-4">
                  {phase === 'credentials' ? (
                    isCrm && branding.markSrc ? (
                      <img
                        src={branding.markSrc}
                        alt={branding.brandName}
                        className="h-9 w-9 object-contain"
                      />
                    ) : (
                      <BrandIcon className="h-10 w-10 text-white" />
                    )
                  ) : phase === 'force-password-change' ? (
                    <ShieldCheck className="h-10 w-10 text-white" />
                  ) : (
                    <KeyRound className="h-10 w-10 text-white" />
                  )}
                </div>
                <h2 className="text-2xl font-bold text-white">
                  {phase === 'credentials'
                    ? branding.formHeading
                    : phase === 'totp-setup'
                    ? 'Configura il 2FA'
                    : phase === 'force-password-change'
                    ? 'Cambio password obbligatorio'
                    : 'Verifica a due fattori'}
                </h2>
                <p className="text-sm text-white/80 mt-1">
                  {phase === 'credentials'
                    ? branding.formSubtitle
                    : phase === 'totp-setup'
                    ? 'L\'autenticazione a due fattori è obbligatoria: configurala per continuare'
                    : phase === 'force-password-change'
                    ? (forceChangeUserName
                        ? `Ciao ${forceChangeUserName}, imposta una nuova password per continuare`
                        : 'Imposta una nuova password per continuare')
                    : phase === '2fa-backup'
                    ? 'Inserisci uno dei codici di backup salvati al setup'
                    : 'Inserisci il codice a 6 cifre dell\'app di autenticazione'}
                </p>
              </div>

              {/* Errors */}
              {error && phase === 'credentials' && (
                <Alert variant="destructive" className="mb-6">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Phase: credentials */}
              {phase === 'credentials' && (
                <form onSubmit={handleSubmit} className="space-y-5 animate-in fade-in duration-200">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-white">Email</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
                      <Input
                        id="email"
                        type="email"
                        required
                        className={`${INPUT_TOKENIZED} pl-10`}
                        placeholder="Inserisci la tua email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-medium text-white">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        className={`${INPUT_TOKENIZED} pl-10 pr-10`}
                        placeholder="Inserisci la tua password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors z-10"
                        disabled={loading}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="text-xs text-white/70 hover:text-white underline underline-offset-2 transition-colors"
                      onClick={() => {
                        setForgotEmail(email);
                        setForgotDone(false);
                        setForgotError(null);
                        setForgotOpen(true);
                      }}
                      disabled={loading}
                    >
                      Password dimenticata?
                    </button>
                  </div>

                  <Button
                    type="submit"
                    className={`w-full bg-white ${accentText} hover:bg-muted font-semibold py-3
                              hover:scale-[1.02] transition-all duration-200 shadow-lg`}
                    disabled={loading}
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Accesso in corso...</>
                    ) : (
                      <><Shield className="mr-2 h-4 w-4" />Accedi</>
                    )}
                  </Button>
                </form>
              )}

              {/* Task #3585 — richiesta reset password self-service */}
              {phase === 'credentials' && forgotOpen && (
                <div className="mt-5 rounded-xl bg-card text-card-foreground p-4 animate-in fade-in duration-200">
                  {forgotDone ? (
                    <div className="space-y-3">
                      <p className="text-sm">
                        Se l'email corrisponde a un account master, riceverai un link
                        per reimpostare la password. Controlla la casella di posta.
                      </p>
                      <Button variant="outline" className="w-full" onClick={() => setForgotOpen(false)}>
                        Chiudi
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleForgotSubmit} className="space-y-3">
                      <Label htmlFor="forgot-email" className="text-sm font-medium">
                        Email dell'account master
                      </Label>
                      <Input
                        id="forgot-email"
                        type="email"
                        required
                        placeholder="Inserisci la tua email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        disabled={forgotBusy}
                      />
                      {forgotError && (
                        <p className="text-xs text-red-600">{forgotError}</p>
                      )}
                      <div className="flex gap-2">
                        <Button type="submit" className="flex-1" disabled={forgotBusy}>
                          {forgotBusy ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Invio…</>
                          ) : (
                            <><KeyRound className="mr-2 h-4 w-4" />Invia link di reset</>
                          )}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setForgotOpen(false)} disabled={forgotBusy}>
                          Annulla
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {/* Phase: 2FA TOTP */}
              {phase === '2fa-totp' && (
                <form onSubmit={handle2FASubmit} className="space-y-5 animate-in fade-in duration-200">
                  <div className="rounded-xl bg-card text-card-foreground p-4">
                    <Label className="text-sm font-medium text-card-foreground mb-3 block text-center">
                      Codice a 6 cifre
                    </Label>
                    <TotpCodeInput
                      value={totpCode}
                      onChange={(v) => { setTotpCode(v); setTwoFAError(null); }}
                      onComplete={(code) => { void performLogin(false, code); }}
                      autoFocus
                      disabled={loading}
                      error={twoFAError}
                    />
                    {attemptsRemaining !== null && twoFAError && (
                      <p className="mt-2 text-xs text-amber-700 dark:text-amber-300 text-center">
                        Tentativi rimasti: {attemptsRemaining}
                      </p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className={`w-full bg-white ${accentText} hover:bg-muted font-semibold py-3
                              hover:scale-[1.02] transition-all duration-200 shadow-lg`}
                    disabled={loading || totpCode.replace(/\s/g, '').length !== 6}
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifica in corso...</>
                    ) : (
                      <><KeyRound className="mr-2 h-4 w-4" />Verifica</>
                    )}
                  </Button>
                  <div className="flex items-center justify-between text-sm">
                    <button
                      type="button"
                      onClick={resetToCredentials}
                      className="text-white/80 hover:text-white inline-flex items-center gap-1 transition-colors"
                      disabled={loading}
                    >
                      <ArrowLeft className="h-3.5 w-3.5" />
                      Indietro
                    </button>
                    <button
                      type="button"
                      onClick={() => { setPhase('2fa-backup'); setTwoFAError(null); }}
                      className="text-white/80 hover:text-white underline-offset-2 hover:underline transition-colors"
                      disabled={loading}
                    >
                      Usa un backup code
                    </button>
                  </div>
                </form>
              )}

              {/* Phase: 2FA backup */}
              {phase === '2fa-backup' && (
                <form onSubmit={handle2FASubmit} className="space-y-5 animate-in fade-in duration-200">
                  <div className="space-y-2">
                    <Label htmlFor="backup" className="text-sm font-medium text-white">Codice di backup</Label>
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
                      <Input
                        id="backup"
                        type="text"
                        inputMode="text"
                        autoComplete="one-time-code"
                        autoFocus
                        maxLength={9}
                        className={`${INPUT_TOKENIZED} pl-10 font-mono tracking-wider uppercase`}
                        placeholder="es. A1B2C3D4 (8 caratteri)"
                        value={backupCode}
                        onChange={(e) => {
                          const cleaned = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
                          setBackupCode(cleaned);
                          setTwoFAError(null);
                        }}
                        disabled={loading}
                      />
                    </div>
                    <p className="text-[11px] text-white/70">8 caratteri alfanumerici (lettere/numeri).</p>
                    {twoFAError && (
                      <Alert variant="destructive" role="alert">
                        <AlertDescription>{twoFAError}</AlertDescription>
                      </Alert>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className={`w-full bg-white ${accentText} hover:bg-muted font-semibold py-3
                              hover:scale-[1.02] transition-all duration-200 shadow-lg`}
                    disabled={loading || !/^[A-Z0-9]{8}$/.test(backupCode.trim())}
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifica in corso...</>
                    ) : (
                      <><KeyRound className="mr-2 h-4 w-4" />Verifica</>
                    )}
                  </Button>
                  <div className="flex items-center justify-between text-sm">
                    <button
                      type="button"
                      onClick={resetToCredentials}
                      className="text-white/80 hover:text-white inline-flex items-center gap-1 transition-colors"
                      disabled={loading}
                    >
                      <ArrowLeft className="h-3.5 w-3.5" />
                      Indietro
                    </button>
                    <button
                      type="button"
                      onClick={() => { setPhase('2fa-totp'); setTwoFAError(null); }}
                      className="text-white/80 hover:text-white underline-offset-2 hover:underline transition-colors"
                      disabled={loading}
                    >
                      Usa codice TOTP
                    </button>
                  </div>
                </form>
              )}

              {/* Phase: forced TOTP enrollment */}
              {phase === 'totp-setup' && setupData && (
                <div className="space-y-5 animate-in fade-in duration-200">
                  <div className="rounded-xl bg-card text-card-foreground p-4 space-y-4">
                    {setupStep === 0 && (
                      <>
                        <TotpQrSetup
                          qrCodeDataUri={setupData.qrCodeDataUri}
                          manualEntryKey={setupData.manualEntryKey}
                        />
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" onClick={cancelForcedSetup} disabled={setupBusy}>
                            Annulla
                          </Button>
                          <Button onClick={() => setSetupStep(1)} disabled={setupBusy}>
                            Ho scansionato il QR
                          </Button>
                        </div>
                      </>
                    )}

                    {setupStep === 1 && (
                      <>
                        <Label className="text-sm font-medium text-card-foreground mb-1 block text-center">
                          Inserisci il codice a 6 cifre dell'app
                        </Label>
                        <TotpCodeInput
                          value={setupCode}
                          onChange={(v) => { setSetupCode(v); setSetupError(null); }}
                          onComplete={(code) => { void confirmForcedEnable(code); }}
                          autoFocus
                          disabled={setupBusy}
                          error={setupError}
                        />
                        <div className="flex justify-between gap-2">
                          <Button variant="ghost" onClick={() => setSetupStep(0)} disabled={setupBusy}>
                            Indietro
                          </Button>
                          <Button
                            onClick={() => { void confirmForcedEnable(setupCode); }}
                            disabled={setupBusy || setupCode.replace(/\s/g, '').length !== 6}
                          >
                            {setupBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            Conferma e attiva
                          </Button>
                        </div>
                      </>
                    )}

                    {setupStep === 2 && (
                      <>
                        <BackupCodesViewer
                          codes={setupData.backupCodes}
                          requireAck
                          acknowledged={setupAck}
                          onAcknowledgeChange={setSetupAck}
                        />
                        <div className="flex justify-end">
                          <Button onClick={finishForcedSetup} disabled={!setupAck}>
                            Fine
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Phase: forced password change */}
              {phase === 'force-password-change' && (
                <form
                  onSubmit={(e) => { e.preventDefault(); void handleForcePasswordChange(); }}
                  className="space-y-5 animate-in fade-in duration-200"
                >
                  {changePwdError && (
                    <Alert variant="destructive">
                      <AlertDescription>{changePwdError}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="new-password" className="text-sm font-medium text-white">
                      Nuova password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
                      <Input
                        id="new-password"
                        type={showNewPassword ? 'text' : 'password'}
                        required
                        autoFocus
                        className={`${INPUT_TOKENIZED} pl-10 pr-10 font-mono`}
                        placeholder="Inserisci la nuova password"
                        value={newPassword}
                        onChange={(e) => { setNewPassword(e.target.value); setChangePwdError(null); }}
                        disabled={changePwdBusy}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors z-10"
                        disabled={changePwdBusy}
                      >
                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password" className="text-sm font-medium text-white">
                      Conferma password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
                      <Input
                        id="confirm-password"
                        type={showNewPassword ? 'text' : 'password'}
                        required
                        className={`${INPUT_TOKENIZED} pl-10 font-mono`}
                        placeholder="Ripeti la nuova password"
                        value={confirmPassword}
                        onChange={(e) => { setConfirmPassword(e.target.value); setChangePwdError(null); }}
                        disabled={changePwdBusy}
                      />
                    </div>
                    {confirmPassword.length > 0 && confirmPassword !== newPassword && (
                      <p className="text-xs text-white/90 flex items-center gap-1">
                        <XIcon className="h-3.5 w-3.5" /> Le password non coincidono
                      </p>
                    )}
                    {confirmPassword.length > 0 && confirmPassword === newPassword && (
                      <p className="text-xs text-white/90 flex items-center gap-1">
                        <CheckCircle className="h-3.5 w-3.5" /> Le password coincidono
                      </p>
                    )}
                  </div>

                  {newPassword && (
                    <div className="rounded-xl bg-white/10 p-3 space-y-1.5">
                      <p className="text-xs font-semibold text-white/90 mb-2">Criteri di sicurezza:</p>
                      {PASSWORD_CRITERIA.map((criterion) => {
                        const passed = criterion.test(newPassword);
                        return (
                          <div key={criterion.key} className="flex items-center gap-1.5 text-xs">
                            {passed ? (
                              <CheckCircle className="h-3.5 w-3.5 text-green-300 shrink-0" />
                            ) : (
                              <XIcon className="h-3.5 w-3.5 text-white/50 shrink-0" />
                            )}
                            <span className={passed ? 'text-green-200' : 'text-white/60'}>
                              {criterion.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <Button
                    type="submit"
                    className={`w-full bg-white ${accentText} hover:bg-muted font-semibold py-3
                              hover:scale-[1.02] transition-all duration-200 shadow-lg`}
                    disabled={
                      changePwdBusy
                      || !PASSWORD_CRITERIA.every((c) => c.test(newPassword))
                      || newPassword !== confirmPassword
                      || confirmPassword.length === 0
                    }
                  >
                    {changePwdBusy ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Aggiornamento in corso...</>
                    ) : (
                      <><ShieldCheck className="mr-2 h-4 w-4" />Imposta nuova password</>
                    )}
                  </Button>

                  <div className="text-center text-sm">
                    <button
                      type="button"
                      onClick={cancelForcePasswordChange}
                      className="text-white/80 hover:text-white inline-flex items-center gap-1 transition-colors"
                      disabled={changePwdBusy}
                    >
                      <ArrowLeft className="h-3.5 w-3.5" />
                      Torna al login
                    </button>
                  </div>
                </form>
              )}

              {/* Footer */}
              <div className="mt-8 pt-6 border-t border-white/20">
                <div className="text-center text-sm text-white/80">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Globe className="h-4 w-4" />
                    <span>{branding.footerTitle}</span>
                  </div>
                  <p className="text-white/60">{branding.footerSubtitle}</p>
                </div>
                <div className="mt-4 text-center">
                  <span className="text-xs text-white/60">
                    crafted by{' '}
                    <a
                      href="https://www.easydigitalgroup.it"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/80 hover:text-white transition-colors inline-flex items-center gap-0.5 hover:underline"
                    >
                      easydigitalgroup
                      <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={showActiveSessionDialog} onOpenChange={setShowActiveSessionDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-300" />
              </div>
              <AlertDialogTitle className="text-lg">Sessione già attiva</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-muted-foreground">
              Il tuo account risulta già connesso da un altro dispositivo o browser.
              Continuando, la sessione precedente verrà chiusa automaticamente e
              l'altro dispositivo verrà disconnesso.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowActiveSessionDialog(false)}>
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleForceLogin}
              className="bg-purple-600 hover:bg-purple-700 text-white dark:bg-purple-500 dark:hover:bg-purple-600"
            >
              Continua e disconnetti l'altra sessione
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
